import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button,
    DialogTitle, DialogContent, DialogActions, Dialog, TextField, DialogContentText
} from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import * as Yup from 'yup';
import CampaignsList from '../components/CampaignsList';
import axios from 'src/services/ApiService'
import { Campaign } from '../types/apps/Campaign';
import { Icon } from '@iconify/react';
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import { ChannelType } from '../types/apps/channelTypes';
import MediumBadge from '../components/MediumBadge';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { getCampaigns } from '../store/apps/campaign';

const validationSchema = Yup.object().shape({
    campaignName: Yup.string()
        .required('Campaign name is required.'),
    startDate: Yup.date()
        .nullable()
        .test('startDate', 'Start date is required if end date is given', function (value) {
            const { endDate } = this.parent;
            if (!value && endDate) {
                return false;
            }
            return true;
        }),
    endDate: Yup.date()
        .nullable()
        .test('endDate', 'End date is required if start date is given', function (value) {
            const { startDate } = this.parent;
            if (!value && startDate) {
                return false;
            }
            return true;
        })
        .test('dates', 'End date cannot be before the start date', function (value) {
            const { startDate } = this.parent;
            if (startDate && value && new Date(startDate) > new Date(value)) {
                return false;
            }
            return true;
        })
});


interface ChannelProps {
    channels: ChannelType[];
    onChannelChange: (channelId: number) => void
}

const SelectChannel: React.FC<ChannelProps> = ({ channels, onChannelChange }) => {
    const [channel, setChannel] = useState<ChannelType>();

    const handleChange = (event: SelectChangeEvent) => {
        const value = Number(event.target.value);
        onChannelChange(value);
        setChannel(channels.find(c => c.id === value) || channels[0]);
    };

    return (
        <div>
            <FormControl fullWidth>
                <InputLabel id="channel-label">Channel</InputLabel>
                <Select
                    labelId="channel-label"
                    value={channel ? channel.id.toString() : ''}
                    onChange={handleChange}
                    label="Channel"
                    size={"small"}
                >
                    {channels.map((channel: ChannelType) => (
                        <MenuItem key={channel.id} value={channel.id}>
                            <Box display="flex" alignItems="center">
                                <span style={{ marginRight: '10px' }}>{channel.name}</span>
                                {channel.profileImageUrl && (
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={<MediumBadge channelTypeName={channel.channelTypeName} mediumName={channel.mediumName} />}
                                        sx={{
                                            '.MuiBadge-badge': {
                                                borderRadius: '50%',
                                                padding: '6px',
                                            }
                                        }}
                                    >
                                        <Avatar src={channel.profileImageUrl} alt={channel.name} />
                                    </Badge>
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
};


const CampaignManagement: React.FC = () => {

    const [open, setOpen] = useState(false);
    const [campaignName, setCampaignName] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [validationMsgs, setValidationMsgs] = useState<string[]>([]);
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    const [valid, setValid] = useState(false);
    const originalCampaigns = useRef<Campaign[]>([]);
    const [channels, setChannels] = useState<ChannelType[]>([]);

    const dispatch = useDispatch<AppDispatch>();
    const campaigns = useSelector((state: RootState) => state.campaign.campaigns);
    const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);


    //const filterPostsByChannel = useCallback((channelId: number) => {
    //    // Check if a specific channel is selected
    //    if (channelId) {
    //        // Filter campaigns to include only posts that match the selected channel
    //        return campaigns.map(campaign => ({
    //            ...campaign,
    //            posts: campaign.posts ? campaign.posts.filter(post => post.channelId === channelId) : []
    //        }));
    //    } else {
    //        // If no specific channel is selected, return all campaigns
    //        return campaigns;
    //    }
    //}, [campaigns]);




    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const fetchChannels = async () => {
        try {
            const response = await axios.get<ChannelType[]>('channel/GetAllAvailableChannels');
            setChannels(response.data);
        } catch (error) {
            console.error('An error occurred while fetching the channels:', error);
        }
    };


    const saveCampaign = async () => {
        try {
            const payload = {
                Name: campaignName,
                StartDate: startDate,
                EndDate: endDate
            };

            await axios.post('campaign/SaveCampaign', payload);
            dispatch(getCampaigns());
            handleClose();
            // Optionally, refresh campaigns list or handle UI updates here
        } catch (error) {
            console.error('An error occurred while saving the campaign:', error);
        }
    };


    const validate = useCallback(() => {
        (async () => {
            try {
                await validationSchema.validate(
                    { campaignName, startDate, endDate },
                    { abortEarly: false }
                );
                setValid(true);
                setValidationMsgs([]);
            } catch (err) {
                setValid(false);
                if (err instanceof Yup.ValidationError) {
                    const messages: string[] = [];
                    err.inner.forEach((error) => {
                        if (touched[error.path!]) {
                            messages.push(error.message);
                        }
                    });
                    setValidationMsgs(messages);
                }
            }
        })();
    }, [campaignName, startDate, endDate, touched]);

    useEffect(() => {
        dispatch(getCampaigns());
        fetchChannels();
    }, []);

    useEffect(() => {
        console.log('useEffect triggered:', { campaignName, startDate, endDate, touched });
        validate();
    }, [campaignName, startDate, endDate, touched, validate]);

    return (

        <Box sx={{ m: 2 }}>
            <Box sx={{ m: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleClickOpen} sx={{ mb: 2 }}>
                    Add Campaign
                </Button>
                <SelectChannel channels={channels} onChannelChange={setSelectedChannelId} />
{/*                <SelectChannel channels={channels} filterPostsByChannel={filterPostsByChannel}></SelectChannel>*/}
            </Box>
            <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '10px',
                    padding: '20px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                }
            }}>
                <DialogTitle id="form-dialog-title" sx={{ mb: 2 }}>Create Your Next Campaign</DialogTitle>
                <DialogContentText>
                    Start your next successful campaign journey by giving it a meaningful name.
                </DialogContentText>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Campaign Name"
                        type="text"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        onBlur={() => setTouched({ ...touched, campaignName: true })}
                        required
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <DatePicker
                                label="Campaign Start Date"
                                inputFormat="MM/dd/yyyy"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    setTouched({ ...touched, startDate: true });
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                            <DatePicker
                                label="Campaign End Date"
                                inputFormat="MM/dd/yyyy"
                                value={endDate}
                                onChange={(newValue) => {
                                    setEndDate(newValue);
                                    setTouched({ ...touched, endDate: true });
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />

                        </Box>
                    </LocalizationProvider>
                </DialogContent>
                {validationMsgs.length > 0 && (
                    <Box sx={{ mb: 2, ml: 2 }}>
                        {validationMsgs.map((msg, index) => (
                            <Typography key={index} variant="body2" color="error">
                                {msg}
                            </Typography>
                        ))}
                    </Box>
                )}
                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant="outlined" sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={valid ? saveCampaign : undefined} color="primary" variant="contained" disabled={!valid}>
                        Save Campaign
                    </Button>
                </DialogActions>
            </Dialog>
            <CampaignsList campaigns={campaigns.filter(c => !selectedChannelId || c.posts?.some(p => p.channelId === selectedChannelId))} />
        </Box>
    );
}




export default CampaignManagement;
